import React, { useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Button } from '../../../../common'
import { BsCircleFill, BsArrowRepeat } from 'react-icons/bs'
import { QRCodeSVG } from 'qrcode.react';
import { ImFileZip } from 'react-icons/im';
import { generateSalesMaterial } from '../../../../queries/booklet'
import { generateSalesMaterialUtil, downloadZipFile, MediaType } from '../../utils/mediaGenerator'
import { useDashboardAssociationContext } from '../../../../context/DashboardAssociationContext'
import JSZip from 'jszip'
import { copyZipFilesToMaster } from './helpers/zipHelpers'
import { generateQRCode as generateQRCodeHelper } from './helpers/qrCodeGenerator'
import { generateMobileImages } from './helpers/mobileImagesGenerator'
import { generatePosterRollup, addPosterRollupToMaster } from './helpers/posterRollupGenerator'
import { generateResellerMaterials } from './helpers/resellerGenerator'
import { buildBookletFolders } from '../../utils/bookletFolderUtils'

enum MarketingType {
  QR_CODE = 'QR_CODE',
  SOCIAL_MEDIA_IMAGES = 'SOCIAL_MEDIA_IMAGES',
  MOBILE_IMAGES = 'MOBILE_IMAGES',
  POSTER = 'POSTER',
  ROLLUP = 'ROLLUP',
  POSTER_AND_ROLLUP = 'POSTER_AND_ROLLUP',
  CAMPAIGN_IMAGES = 'CAMPAIGN_IMAGES',
  RESELLER = 'RESELLER'
}

interface MarketingMaterial {
  type: MarketingType
  label: string
  mainAssociationOnly: boolean
}

const MARKETING_MATERIALS: MarketingMaterial[] = [
  {
    type: MarketingType.QR_CODE,
    label: 'QR-kod',
    mainAssociationOnly: false
  },
  {
    type: MarketingType.SOCIAL_MEDIA_IMAGES,
    label: 'Bilder till sociala medier',
    mainAssociationOnly: false
  },
  {
    type: MarketingType.POSTER,
    label: 'Affisch',
    mainAssociationOnly: false
  },
  {
    type: MarketingType.MOBILE_IMAGES,
    label: 'Bilder på häftet',
    mainAssociationOnly: false
  },
  {
    type: MarketingType.POSTER_AND_ROLLUP,
    label: 'Affisch och roll-up',
    mainAssociationOnly: true
  },
  {
    type: MarketingType.RESELLER,
    label: 'Affischer för återförsäljare',
    mainAssociationOnly: true
  }
]
interface MarketingStatusIconProps {
  type: MarketingType
  currentDownload: MarketingType | null
  disabled?: boolean
}

interface MarketingSectionProps {
  isMainAssociation: boolean
}

const MarketingSection: React.FC<MarketingSectionProps> = ({ isMainAssociation }) => {
  const { associationId, associationData, associationBooklets, shopShareDomain } = useDashboardAssociationContext()
  const [generateSalesMaterialMutation] = useMutation(generateSalesMaterial)
  const [currentDownload, setCurrentDownload] = useState<MarketingType | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)

  const qrCodeRef = useRef<HTMLDivElement>(null);
  const qrCodeUrl = `${shopShareDomain}/${associationData?.slug}`;

  // Filter marketing materials based on association type
  const availableMarketingMaterials = MARKETING_MATERIALS.filter(
    material => {
      // Only show if it's available for this association type
      if (material.mainAssociationOnly && !isMainAssociation) return false

      // For main associations, hide POSTER if POSTER_AND_ROLLUP is available
      if (isMainAssociation && material.type === MarketingType.POSTER) {
        const hasPosterAndRollup = MARKETING_MATERIALS.some(
          m => m.type === MarketingType.POSTER_AND_ROLLUP && (!m.mainAssociationOnly || isMainAssociation)
        )
        if (hasPosterAndRollup) return false
      }

      return true
    }
  )

  // Helper function to check if a material type is available
  const isMarketingTypeAvailable = (type: MarketingType): boolean => {
    return availableMarketingMaterials.some(material => material.type === type)
  }

  // Helper function to check if POSTER_AND_ROLLUP should generate both
  const shouldGeneratePosterAndRollup = (): boolean => {
    return availableMarketingMaterials.some(material => material.type === MarketingType.POSTER_AND_ROLLUP)
  }

  const handleGenerateAndDownload = async () => {
    if (!associationData || !associationId) {
      console.error('Association data not available')
      return
    }

    setIsGenerating(true)
    try {
      const masterZip = new JSZip()
      const bookletFolders = buildBookletFolders(associationBooklets)

      // 1. QR-kod
      if (isMarketingTypeAvailable(MarketingType.QR_CODE)) {
        setCurrentDownload(MarketingType.QR_CODE)
        try {
          const qrZip = await generateQRCodeHelper({
            qrCodeRef,
            associationName: associationData.name
          })
          await copyZipFilesToMaster(qrZip, masterZip, 'QR-kod/', {
            duplicateToEachBooklet: true,
            bookletFolders
          })
        } catch (error) {
          console.error('Error generating QR code:', error)
        }
      }

      // 2. Bilder till sociala medier
      if (isMarketingTypeAvailable(MarketingType.SOCIAL_MEDIA_IMAGES)) {
        setCurrentDownload(MarketingType.SOCIAL_MEDIA_IMAGES)
        try {
          const { zip: socialMediaZip } = await generateSalesMaterialUtil(
            associationBooklets,
            associationId,
            MediaType.SOCIAL_MEDIA,
            generateSalesMaterialMutation
          )
          await copyZipFilesToMaster(socialMediaZip, masterZip, 'Marknadsför häftet/Bilder i sociala medie-format/', {
            bookletFolders
          })
        } catch (error) {
          console.error('Error generating social media images:', error)
        }
      }

      // 3. Bilder på häftet
      if (isMarketingTypeAvailable(MarketingType.MOBILE_IMAGES)) {
        setCurrentDownload(MarketingType.MOBILE_IMAGES)
        try {
          const mobileZip = await generateMobileImages({
            associationBooklets
          })
          await copyZipFilesToMaster(mobileZip, masterZip, 'Marknadsför häftet/Mobilbilderna/', {
            bookletFolders
          })
        } catch (error) {
          console.error('Error downloading mobile images:', error)
        }
      }

      // 4. Affisch och roll-up (kombinerad punkt)
      if (shouldGeneratePosterAndRollup()) {
        setCurrentDownload(MarketingType.POSTER_AND_ROLLUP)
        try {
          const { posterZip, rollupZip } = await generatePosterRollup({
            associationBooklets,
            associationId,
            generateSalesMaterialMutation,
            generatePoster: true,
            generateRollup: true
          })
          await addPosterRollupToMaster(masterZip, posterZip, rollupZip, bookletFolders)
        } catch (error) {
          console.error('Error generating poster and rollup:', error)
        }
      }

      // 5. Affisch (för vanliga föreningar - när det inte är POSTER_AND_ROLLUP)
      if (isMarketingTypeAvailable(MarketingType.POSTER) && !shouldGeneratePosterAndRollup()) {
        setCurrentDownload(MarketingType.POSTER)
        try {
          const { posterZip } = await generatePosterRollup({
            associationBooklets,
            associationId,
            generateSalesMaterialMutation,
            generatePoster: true,
            generateRollup: false
          })
          if (posterZip) {
            await copyZipFilesToMaster(posterZip, masterZip, 'Marknadsför häftet/Affisch med QR-kod till föreningens webbshop/', {
              bookletFolders
            })
          }
        } catch (error) {
          console.error('Error generating posters:', error)
        }
      }

      // 6. Återförsäljare
      if (isMarketingTypeAvailable(MarketingType.RESELLER)) {
        setCurrentDownload(MarketingType.RESELLER)
        try {
          await generateResellerMaterials({
            associationBooklets,
            associationId,
            generateSalesMaterialMutation,
            masterZip,
            bookletFolders
          })
        } catch (error) {
          console.error('Error generating reseller materials:', error)
        }
      }

      setCurrentDownload(null)
      await downloadZipFile(masterZip, `${associationData.name}-marknadsforing.zip`)
    } finally {
      setIsGenerating(false)
      setCurrentDownload(null)
    }
  }

  return (
    <div className="resource-section">
      <div ref={qrCodeRef} style={{ display: 'none' }}>
        <QRCodeSVG
          value={qrCodeUrl}
          size={400}
          bgColor="#ffffff"
          fgColor="#000000"
          level="H"
          includeMargin={false}
        />
      </div>
      <div className='box md flex flex-col text-left gap-2'>
        <h3>{isMainAssociation ? 'Marknadsföring' : 'Säljmaterial'}</h3>
        <div>
          <p>
            {isMainAssociation
              ? 'Här hittar du material som hjälper er att marknadsföra försäljningen.'
              : 'Här hittar du affischer och bilder att använda vid säljaktiviteter — både IRL och på sociala medier.'
            }
          </p>
          <ul className="sales-material-list">
            {availableMarketingMaterials.map((material) => (
              <li key={material.type}>
                <MarketingStatusIcon
                  type={material.type}
                  currentDownload={currentDownload}
                />
                <span>{material.label}</span>
              </li>
            ))}
          </ul>
          <p><b>Tips!</b> Mappen tar en stund att ladda ned, låt fliken vara öppen tills nedladdningen är klar. Om zip-filen inte kan öppnas i mobilen, prova på datorn.</p>
        </div>
        <Button
          onClick={handleGenerateAndDownload}
          className="primary alt md"
          loading={isGenerating}
          disabled={isGenerating || !associationBooklets.length}
        >
          <div className="flex gap-1 items-center justify-center">
            <span>Ladda ned .zip</span>
            <ImFileZip />
          </div>
        </Button>
        {!associationBooklets.length && (
          <p className="text-sm text-gray-500">
            Inga häften tillgängliga för denna förening.
          </p>
        )}
      </div>
    </div>
  )
}

const MarketingStatusIcon: React.FC<MarketingStatusIconProps> = ({ type, currentDownload = false }) => {
  if (currentDownload === type) {
    return (
      <div className="icon-container">
        <BsArrowRepeat className="spinner" />
      </div>
    )
  }
  return (
    <div className="icon-container">
      <BsCircleFill className="dot" />
    </div>
  )
}

export default MarketingSection
